'use client';

import Decimal from 'decimal.js';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { InfoIcon } from '@/lib/components/InfoIcon';
import { PercentText } from '@/lib/components/Text/PercentText';
import { USDText } from '@/lib/components/Text/USDText';
import { useTradingBalances } from '@/lib/components/Trading/TradingDataContext';
import { useOrderBookDataById } from '@/lib/hooks/useOrderBookServiceById';
import { useFees } from '@/lib/providers/FeesProvider';
import { useOrderFormStore, useSelectedOutcome } from '@/lib/store/order-form-store';
import { EMarketAction } from '@/lib/types/market-action';
import { EMarketIntent } from '@/lib/types/market-intent';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import {
  calculateAvgMarketBuyPrice,
  calculateAvgMarketSellPrice,
  calculateMarketBuyShares,
  calculateMarketSellProceeds,
} from '@/lib/utils/calculateAvgMarketPrice';
import { proceedsAfterFee, SHARE_DECIMALS, spendableAfterFee } from '@/lib/utils/calculateFees';
import { formatCents, formatMoney } from '@/lib/utils/formatAmount';
import { floorTo } from '@/lib/utils/numbers';

interface MarketConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing?: boolean;
}

type RowProps = {
  label: string;
  tooltip?: string;
  value: React.ReactNode;
  valueClassName?: string;
};

const Row = ({ label, tooltip, value, valueClassName }: RowProps) => (
  <div className="flex h-6 items-center justify-between">
    <div className="flex items-center gap-1">
      <span className="text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary opacity-50">
        {label}
      </span>
      {tooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex items-center">
              <InfoIcon className="ml-0 block opacity-50" />
            </span>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            sideOffset={8}
            className="max-w-[280px] px-2 py-1.5 !text-left text-[12px] leading-4"
          >
            {tooltip}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
    <span
      className={cn(
        'text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary',
        valueClassName,
      )}
    >
      {value}
    </span>
  </div>
);

export const MarketConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing = false,
}: MarketConfirmDialogProps) => {
  const t = useTranslations('trade.confirmDialog');
  const tCommon = useTranslations('common');
  const selectedOutcome = useSelectedOutcome();
  const event = useOrderFormStore.use.event();
  const selectedAction = useOrderFormStore.use.action();
  const intent = useOrderFormStore.use.intent();
  const amountUsd = useOrderFormStore.use.amountUsd();
  const sharesCount = useOrderFormStore.use.sharesCount();
  const orderBook = useOrderBookDataById(selectedOutcome?.id ?? null);
  const balancesData = useTradingBalances();
  const { fees } = useFees();

  // The backend fits `cost + taker fee` into the entered amount, so the sweep runs on
  // what is left for shares — otherwise the estimate promises ~0.5% more than fills.
  const spendable = useMemo(
    () => (fees ? spendableAfterFee(amountUsd, fees) : amountUsd),
    [amountUsd, fees],
  );

  const isBuy = selectedAction === EMarketAction.BUY;
  const isYes = intent === EMarketIntent.YES;

  // Cost basis of the shares being sold (avgOpenPrice × shares).
  // avgOpenPrice comes from the user's balance — same source as the Open Positions table.
  const sellCostBasis = useMemo(() => {
    if (isBuy || !selectedOutcome) return null;
    const resolution = selectedOutcome.r?.find((r) => r.isPositive === isYes);
    const tokenId = resolution?.id;
    if (!tokenId) return null;
    const balance = balancesData?.balances.find((b) => b.eventTokenId === tokenId);
    if (balance?.averageOpenPrice === undefined) return null;
    return new Decimal(balance.averageOpenPrice).mul(sharesCount);
  }, [isBuy, isYes, selectedOutcome, balancesData, sharesCount]);

  const estPrice = useMemo(() => {
    if (!orderBook || !selectedOutcome || !orderBook[intent]) return Decimal(0);
    if (isBuy) {
      const asks = orderBook[intent].asks || [];
      return calculateAvgMarketBuyPrice(asks, spendable).mul(100);
    }
    const bids = orderBook[intent].bids || [];
    return calculateAvgMarketSellPrice(bids, sharesCount).mul(100);
  }, [orderBook, selectedOutcome, intent, isBuy, spendable, sharesCount]);

  const estShares = useMemo(() => {
    if (isBuy) {
      if (!orderBook || !selectedOutcome || !orderBook[intent]) return Decimal(0);
      return floorTo(
        calculateMarketBuyShares(orderBook[intent].asks || [], spendable),
        SHARE_DECIMALS,
      );
    }
    return sharesCount;
  }, [isBuy, spendable, orderBook, selectedOutcome, intent, sharesCount]);

  // Gross proceeds the user actually receives if they execute the sell right now:
  // sweep avg over the bid side × shares being sold.
  const sellProceeds = useMemo(() => {
    if (isBuy || !orderBook || !selectedOutcome || !orderBook[intent]) return Decimal(0);
    const bids = orderBook[intent].bids;
    if (!bids || bids.length === 0) return Decimal(0);
    // Net of the sell taker fee — this is what actually lands on the balance.
    const gross = calculateMarketSellProceeds(bids, sharesCount);
    return fees ? proceedsAfterFee(gross, fees) : gross;
  }, [isBuy, orderBook, selectedOutcome, intent, sharesCount, fees]);

  // Buy: cost is what the user pays now (amountUsd).
  // Sell: cost = avgOpenPrice × shares (cost basis of the shares being sold).
  // Falls back to proceeds so PnL% stays well-defined when avgOpenPrice is unknown.
  const positionValue = useMemo(() => {
    if (isBuy) return amountUsd;
    return sellCostBasis ?? sellProceeds;
  }, [isBuy, amountUsd, sellCostBasis, sellProceeds]);

  const potentialReturn = useMemo(() => {
    if (isBuy) {
      if (estShares.isZero() || estPrice.isZero() || amountUsd.isZero()) return Decimal(0);
      return estShares.minus(amountUsd);
    }
    // Real PnL on execution = proceeds (sweep) − cost basis. 0 if either is unknown.
    if (!sellCostBasis || sellProceeds.isZero()) return Decimal(0);
    return sellProceeds.minus(sellCostBasis);
  }, [isBuy, estShares, amountUsd, estPrice, sellCostBasis, sellProceeds]);

  const potentialReturnPercent = useMemo(() => {
    if (positionValue.eq(0)) return Decimal(0);
    return potentialReturn.div(positionValue);
  }, [potentialReturn, positionValue]);

  const hasMultipleOutcomes = !!event?.markets && event.markets.length > 1;
  const titleText = useMemo(() => {
    if (hasMultipleOutcomes && selectedOutcome?.groupItemTitle) {
      return selectedOutcome.groupItemTitle;
    }
    return `${t('outcome')} ${isYes ? tCommon('yes') : tCommon('no')}`;
  }, [hasMultipleOutcomes, selectedOutcome, isYes, t, tCommon]);

  // Fees drive the estimate — rendering before they load would show numbers that
  // ignore the 0.5% taker, the very thing this dialog is supposed to get right.
  if (!fees) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="flex w-[400px] max-w-[calc(100vw-32px)] flex-col gap-6 rounded-[16px] border border-v2-surface-tertiary bg-v2-surface-primary p-6"
        showCloseButton={false}
      >
        <DialogTitle className="flex items-center justify-between !pr-0 !text-[24px] !leading-8">
          <span className="flex items-center gap-2">
            <span className="text-[24px] font-semibold leading-8 text-v2-text-primary">
              {titleText}
            </span>
            {hasMultipleOutcomes && (
              <span
                className={cn(
                  'flex items-center justify-center rounded-[8px] px-2 py-0.5 text-[12px] leading-4 tracking-[-0.12px]',
                  isYes ? 'text-v2-green' : 'text-red-400',
                )}
                style={{
                  backgroundColor: isYes ? 'rgba(23, 210, 113, 0.1)' : 'rgba(251, 78, 75, 0.1)',
                }}
              >
                {isYes ? tCommon('yes') : tCommon('no')}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-v2-text-primary transition-opacity hover:opacity-80"
          >
            <Icon name="Close" size={24} />
          </button>
        </DialogTitle>

        <div className="flex flex-col gap-2">
          <Row
            label={t('estPrice')}
            tooltip={t('estPriceTooltip')}
            value={estPrice.gt(0) ? formatCents(estPrice) : '--'}
          />
          <Row
            label={t('estShares')}
            tooltip={t('estSharesMarketTooltip')}
            value={estShares.gt(0) ? formatMoney(estShares.toNumber(), { decimals: 2 }) : '--'}
          />
          <Row
            label={t('potentialReturn')}
            tooltip={isBuy ? t('potentialReturnBuyTooltip') : t('potentialReturnSellTooltip')}
            valueClassName={potentialReturn.gte(0) ? 'text-v2-green' : 'text-red-400'}
            value={
              <>
                <USDText value={potentialReturn} alwaysShowDecimals /> (
                <PercentText value={potentialReturnPercent} decimals={0} />)
              </>
            }
          />
          <Row
            label={t('positionValue')}
            value={<USDText value={positionValue} alwaysShowDecimals />}
          />
        </div>

        <div className="flex min-h-[72px] items-center rounded-[10px] bg-v2-neutral-surface p-4">
          <span className="text-[13px] leading-[1.5] text-v2-text-primary">{t('infoBox')}</span>
        </div>

        <div className="flex w-full items-start gap-3">
          <Button
            appearance="neutral"
            size="M"
            onClick={onClose}
            disabled={isProcessing}
            className="h-10 flex-1"
          >
            {t('cancel')}
          </Button>
          <Button
            appearance="accent"
            size="M"
            onClick={onConfirm}
            disabled={isProcessing}
            className="h-10 flex-1"
          >
            {isProcessing ? tCommon('loading') : t('confirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
